import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuth } from '@/context/AuthContext'
import { useOrg } from '@/context/OrgContext'
import { useCreateUser, useUpdateUser } from '@/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { User, Role } from '@/types'

const userFormSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Invalid email address'),
  name: z.string().max(100, 'Name is too long').optional(),
  role: z.enum(['super_admin', 'admin', 'employee', 'viewer'], {
    message: 'Role is required',
  }),
})

type UserFormValues = z.infer<typeof userFormSchema>

interface UserFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  user?: User
}

const allRoleOptions: { value: Role; label: string; level: number }[] = [
  { value: 'super_admin', label: 'Super Admin', level: 0 },
  { value: 'admin', label: 'Admin', level: 1 },
  { value: 'employee', label: 'Employee', level: 2 },
  { value: 'viewer', label: 'Viewer', level: 3 },
]

const roleLevels: Record<Role, number> = {
  super_admin: 0,
  admin: 1,
  employee: 2,
  viewer: 3,
}

export function UserFormDialog({
  open,
  onOpenChange,
  user: editingUser,
}: UserFormDialogProps) {
  const { user: currentUser } = useAuth()
  const { effectiveOrgId } = useOrg()
  const isEditing = !!editingUser

  const createMutation = useCreateUser(effectiveOrgId)
  const updateMutation = useUpdateUser(editingUser?.id ?? '', effectiveOrgId)

  // Only show roles at or below the current user's level
  const currentLevel = currentUser?.role ? roleLevels[currentUser.role] : 3
  const availableRoles = allRoleOptions.filter((r) => r.level >= currentLevel)

  const form = useForm<UserFormValues>({
    resolver: zodResolver(userFormSchema),
    defaultValues: {
      email: editingUser?.email ?? '',
      name: editingUser?.name ?? '',
      role: editingUser?.role ?? 'employee',
    },
  })

  // Reset form when user changes or dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        email: editingUser?.email ?? '',
        name: editingUser?.name ?? '',
        role: editingUser?.role ?? 'employee',
      })
    }
  }, [open, editingUser, form])

  const isPending = createMutation.isPending || updateMutation.isPending

  function onSubmit(values: UserFormValues) {
    if (isEditing) {
      updateMutation.mutate(
        {
          email: values.email,
          name: values.name || undefined,
          role: values.role,
        },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        }
      )
    } else {
      createMutation.mutate(
        {
          email: values.email,
          name: values.name || '',
          role: values.role,
        },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        }
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit User' : 'Add User'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the user details below.'
              : 'Enter the details for the new user. They will receive an invitation email.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="user@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Full name (optional)" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isEditing
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Add User'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
